<script>
import bem from '@/utils/bem';
import { mapActions, mapState } from 'vuex';

const b = bem('edit-price-modal');

export default {
  props: {
    visible: {
      type: Boolean,
      default: false,
    },
    beforeText: {
      type: String,
      default: '',
    },
    afterText: {
      type: String,
      default: '',
    },
    productId: {
      type: [Number, String],
      default: 0,
    },
    optionId: {
      type: [Number, String],
      default: 0,
    },
    showDelete: {
      type: Boolean,
      default: false,
    },
  },
  emits: ['close', 'save', 'delete'],
  data() {
    return {
      localBeforeText: '',
      localAfterText: '',
      selectedProductId: null,
      selectedOptionId: null,
      products: [],
      optionsForProduct: [],
      mainProduct: null,
      price: 0,
      additionalDiscount: 0,
      isInitializing: false,
    };
  },
  computed: {
    ...mapState({
      productList: (state) => state.products.productList,
    }),
  },
  watch: {
    visible: {
      handler(val) {
        if (val) {
          this.isInitializing = true;
          this.localBeforeText = this.beforeText || '';
          this.localAfterText = this.afterText || '';
          this.selectedProductId = this.productId || null;
          this.selectedOptionId = this.optionId || null;
          this.getProductListForPromoAction();
          this.$nextTick(() => {
            this.isInitializing = false;
          });
        }
      },
      immediate: true,
    },
    productList: {
      handler(newV) {
        if (newV && newV.length) {
          const productsWithOptions = newV.filter((item) => item.options.length > 0);
          this.products = productsWithOptions.map((item) => ({
            label: item.displayName,
            value: item.id,
          }));
          this.checkMainProduct();
        }
      },
      immediate: true,
    },
    selectedProductId(newV) {
      if (newV && this.productList && this.productList.length) {
        this.checkMainProduct();
        // Only reset optionId when user manually changes product, not during initialization
        if (!this.isInitializing) {
          this.selectedOptionId = null;
        }
      }
    },
    selectedOptionId(newV) {
      if (newV && this.mainProduct) {
        const checkedOption = this.mainProduct.options.find((option) => option.id === newV);
        if (checkedOption) {
          this.price = checkedOption.price;
          this.additionalDiscount = checkedOption.additionalDiscount || 0;
        }
      }
    },
  },
  methods: {
    b(firstArg, secondArg) {
      return b(firstArg, secondArg);
    },
    ...mapActions({
      getProductListForPromoAction: 'products/getProductListForPromo',
    }),
    checkMainProduct() {
      if (!this.selectedProductId || !this.productList) return;

      this.mainProduct = this.productList.find((item) => item.id === this.selectedProductId);
      if (this.mainProduct && this.mainProduct.name) {
        if (this.mainProduct.name === 'photo_art') {
          this.optionsForProduct = this.mainProduct.options.map((item) => ({
            label: `${item.frameColor?.name || ''} ${item.frameSize?.name || ''} ${item.frameOrientation || ''}`.trim(),
            value: item.id,
          }));
        } else {
          this.optionsForProduct = this.mainProduct.options.map((item) => ({
            label: item.displayName,
            value: item.id,
          }));
        }
      } else {
        this.optionsForProduct = [];
      }
    },
    clearProduct() {
      this.selectedProductId = null;
      this.selectedOptionId = null;
      this.optionsForProduct = [];
      this.price = 0;
      this.additionalDiscount = 0;
    },
    handleSave() {
      this.$emit('save', {
        beforeText: this.localBeforeText,
        afterText: this.localAfterText,
        productId: this.selectedProductId || 0,
        optionId: this.selectedOptionId || 0,
        price: this.price,
        additionalDiscount: this.additionalDiscount,
      });
    },
    handleClose() {
      this.$emit('close');
    },
    handleDelete() {
      this.$emit('delete');
    },
  },
};
</script>

<template>
  <a-modal
    :open="visible"
    :footer="null"
    width="600px"
    title="Edit Price Block"
    @cancel="handleClose"
  >
    <div :class="b()">
      <div :class="b('field')">
        <span :class="b('label')">Text Before Price</span>
        <a-textarea
          v-model:value="localBeforeText"
          :class="b('textarea')"
          placeholder="Enter text before price (e.g., 'Starting at')"
          :rows="2"
        />
      </div>

      <div :class="b('field')">
        <span :class="b('label')">Product</span>
        <div :class="b('product-row')">
          <a-select
            :class="b('select')"
            :options="products"
            placeholder="Select product..."
            v-model:value="selectedProductId"
            allowClear
          />
          <a-button
            type="primary"
            danger
            @click="clearProduct"
          >
            Clear
          </a-button>
        </div>
      </div>

      <div v-if="optionsForProduct.length" :class="b('field')">
        <span :class="b('label')">Product Option</span>
        <a-select
          :class="b('select')"
          :options="optionsForProduct"
          placeholder="Select option..."
          v-model:value="selectedOptionId"
          allowClear
        />
      </div>

      <div v-if="selectedOptionId && price" :class="b('price-preview')">
        <span :class="b('price-label')">Price Preview:</span>
        <span :class="b('price-value')">
          {{ localBeforeText }}
          <template v-if="additionalDiscount">
            <span :class="b('price-old')">${{ price }}</span>
            <span :class="b('price-new')">${{ price - additionalDiscount }}</span>
          </template>
          <span v-else :class="b('price-new')">${{ price }}</span>
          {{ localAfterText }}
        </span>
      </div>

      <div :class="b('field')">
        <span :class="b('label')">Text After Price</span>
        <a-textarea
          v-model:value="localAfterText"
          :class="b('textarea')"
          placeholder="Enter text after price (e.g., 'per item')"
          :rows="2"
        />
      </div>

      <div :class="b('actions', { withDelete: showDelete })">
        <a-button
          v-if="showDelete"
          type="primary"
          danger
          @click="handleDelete"
        >
          Delete
        </a-button>
        <div :class="b('actions-right')">
          <a-button @click="handleClose">Cancel</a-button>
          <a-button type="primary" @click="handleSave">Save</a-button>
        </div>
      </div>
    </div>
  </a-modal>
</template>

<style scoped lang="scss">
@import '@/styles/variables';
@import '@/styles/media';

.edit-price-modal {
  &__field {
    margin-bottom: 16px;

    @include mediaMinMax(1025, $normal) {
      margin-bottom: vw-full(16);
    }
  }

  &__label {
    display: block;
    font-size: 14px;
    font-weight: 500;
    color: #293241;
    margin-bottom: 8px;

    @include mediaMinMax(1025, $normal) {
      font-size: vw-full(14);
      margin-bottom: vw-full(8);
    }
  }

  &__input,
  &__textarea,
  &__select {
    width: 100%;
  }

  &__product-row {
    display: flex;
    gap: 12px;
    align-items: center;

    @include mediaMinMax(1025, $normal) {
      gap: vw-full(12);
    }
  }

  &__product-row &__select {
    flex: 1;
  }

  &__price-preview {
    background: #f5f5f5;
    border-radius: 8px;
    padding: 16px;
    margin-bottom: 16px;

    @include mediaMinMax(1025, $normal) {
      border-radius: vw-full(8);
      padding: vw-full(16);
      margin-bottom: vw-full(16);
    }
  }

  &__price-label {
    display: block;
    font-size: 12px;
    color: #666;
    margin-bottom: 8px;

    @include mediaMinMax(1025, $normal) {
      font-size: vw-full(12);
      margin-bottom: vw-full(8);
    }
  }

  &__price-value {
    font-size: 16px;
    color: #293241;

    @include mediaMinMax(1025, $normal) {
      font-size: vw-full(16);
    }
  }

  &__price-old {
    text-decoration: line-through;
    color: #999;
    margin-right: 8px;

    @include mediaMinMax(1025, $normal) {
      margin-right: vw-full(8);
    }
  }

  &__price-new {
    color: #1890ff;
    font-weight: 600;
  }

  &__actions {
    display: flex;
    justify-content: flex-end;
    gap: 12px;
    margin-top: 24px;

    @include mediaMinMax(1025, $normal) {
      gap: vw-full(12);
      margin-top: vw-full(24);
    }

    &_withDelete {
      justify-content: space-between;
    }
  }

  &__actions-right {
    display: flex;
    gap: 12px;

    @include mediaMinMax(1025, $normal) {
      gap: vw-full(12);
    }
  }
}
</style>
